import { sanitizeFilename, triggerSecureDownload } from "@repo/mfe-core";
import { useCallback } from "react";
import { DOWNLOAD_TIMEOUT } from "../../../constants/policies.constants";
import { API_BASE_URL, getStoredTokens } from "@repo/api";
import { FindingsRequestPayload } from "@repo/api/schemas/policiesFindings.schema";

// Mirrors the auth-header logic in authenticatedClient.ts:
// localStorage first (standalone mode), cookies as fallback (MFE mode).
const getAuthHeader = (): Record<string, string> => {
  let token = getStoredTokens().accessToken;
  let tokenType = "Bearer";

  if (!token && typeof document !== "undefined") {
    for (const cookie of document.cookie.split(";")) {
      const [name, value] = cookie.trim().split("=");
      if (name === "access_token" || name === "accessToken") {
        token = decodeURIComponent(value);
      }
      if (name === "token_type") {
        tokenType = decodeURIComponent(value) || "Bearer";
      }
    }
  }

  return token ? { Authorization: `${tokenType} ${token}` } : {};
};

export const usePoliciesFindingsExport = (payload: FindingsRequestPayload) => {
  const handleExport = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT);
    try {
      const fullUrl = `${API_BASE_URL}/ispm/v1/finding/export`;
      const urlToCall = fullUrl.startsWith("http")
        ? (() => {
            const u = new URL(fullUrl);
            return u.toString().replace(u.origin, "");
          })()
        : fullUrl;

      const response = await fetch(urlToCall, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const contentDisposition =
        response.headers.get("content-disposition") || "";
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = sanitizeFilename(
        filenameMatch ? filenameMatch[1] : "download.zip",
      );

      triggerSecureDownload(blob, filename);
      console.info("File downloaded successfully:", filename);
    } catch (error) {
      console.error("====Download failed:", error);
    } finally {
      clearTimeout(timeoutId);
    }
  }, [payload]);

  return { handleExport };
};
